// 账号详情页：Tabs（基础 / 授权 / 事件 / 审计 / 模块）。
// 数据源：GET /api/accounts/{id}（account/auths/events/audit/modules）
//        + GET /api/accounts/{id}/modules（drift 详情）

import { createFileRoute, useParams, Link } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { apiGet, apiPostForm, APIError } from "@/api/client";
import {
  accountDetailPath,
  accountLaunchPath,
  accountDownloadDirProbePath,
  type AccountLaunchAction,
  roleLaunchBindingsPath,
  roleLaunchBindingPath,
  accountMemberBenefitRefreshPath,
  accountMarketCDPPath,
  type AccountMarketCDPAction,
  accountProfilePath,
  accountModulesPath,
  accountAuthMetadataPath,
} from "@/api/endpoints";
import type {
  AccountDetailResponse,
  AccountGameCacheEntry,
  AccountGameCacheSlotEntry,
  AccountLaunchActionResponse,
  AccountDownloadDirProbeResponse,
  AccountMemberBenefitRefreshResponse,
  AccountModulesResponse,
  AccountListEntry,
  ModuleMatrixEntry,
  RoleLaunchBindingEntry,
  RoleLaunchBindingsResponse,
} from "@/api/types";
import { QueryView } from "@/components/ui/QueryView";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/Tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Field, Input } from "@/components/ui/Input";
import { Button } from "@/components/ui/Button";
import { InlineEditableCell } from "@/components/forms/InlineEditableCell";
import { AuthActionsCell } from "@/components/forms/AuthActionsCell";
import { toast } from "@/components/ui/Sonner";
import { useSession } from "@/hooks/useSession";
import {
  formatModuleConfigDrift,
  formatModuleMatrixColor,
  formatModuleReportedEnabled,
  formatModuleRuntimeStatus,
} from "@/lib/module-status";
import { formatMs } from "@/lib/format";

export const Route = createFileRoute("/accounts/$accountId/")({
  component: AccountDetailRoute,
});

function AccountDetailRoute() {
  const { accountId } = useParams({ from: "/accounts/$accountId/" });
  return <AccountDetailView accountId={accountId} />;
}

export function AccountDetailView({ accountId }: { accountId: string }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const canWrite = session.data?.can_write ?? false;
  const actor = session.data?.actor ?? "anonymous";
  const [tab, setTab] = useState("base");

  const q = useQuery({
    queryKey: ["account", accountId],
    queryFn: () =>
      apiGet<AccountDetailResponse>(accountDetailPath(accountId)),
  });
  const modulesQ = useQuery({
    queryKey: ["account-modules", accountId],
    queryFn: () =>
      apiGet<AccountModulesResponse>(accountModulesPath(accountId)),
    enabled: tab === "modules",
  });
  const roleBindingsQ = useQuery({
    queryKey: ["role-launch-bindings", "account", accountId],
    queryFn: () =>
      apiGet<RoleLaunchBindingsResponse>(
        roleLaunchBindingsPath({ accountID: accountId }),
      ),
    enabled: tab === "base",
  });

  const saveProfile = async (profile: AccountProfileForm) => {
    try {
      await apiPostForm(accountProfilePath(accountId), profile);
      toast.success("账号资料已保存");
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("保存账号资料失败", { description: msg });
      throw err;
    }
  };

  const runMarketCDPAction = async (action: AccountMarketCDPAction) => {
    try {
      const payload = await apiPostForm<AccountMarketCDPActionResponse>(
        accountMarketCDPPath(accountId, action),
        {},
      );
      const result = payload.result?.result ?? payload.result;
      const status = stringFromUnknown(result?.status);
      const description = [
        status ? `状态 ${status}` : "",
        result?.port ? `端口 ${String(result.port)}` : "",
      ]
        .filter(Boolean)
        .join(" · ");
      if (payload.ok) {
        toast.success(accountMarketCDPActionLabel(action), {
          description: description || undefined,
        });
      } else {
        toast.error(`${accountMarketCDPActionLabel(action)}失败`, {
          description: payload.error || description || "agent 返回失败",
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      return payload;
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error(`${accountMarketCDPActionLabel(action)}失败`, {
        description: msg,
      });
      throw err;
    }
  };

  const refreshMemberBenefit = async () => {
    try {
      const payload = await apiPostForm<AccountMemberBenefitRefreshResponse>(
        accountMemberBenefitRefreshPath(accountId),
        {},
      );
      if (payload.ok) {
        toast.success("会员与充值券已刷新");
      } else {
        toast.error("刷新会员与充值券失败", {
          description: payload.error || "agent 返回失败",
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      return payload;
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("刷新会员与充值券失败", { description: msg });
      throw err;
    }
  };

  const probeDownloadDir = async () => {
    try {
      const payload = await apiPostForm<AccountDownloadDirProbeResponse>(
        accountDownloadDirProbePath(accountId),
        {},
      );
      if (payload.ok) {
        toast.success("下载目录检测完成", {
          description: payload.download_dir || "未返回目录",
        });
      } else {
        toast.error("下载目录检测失败", {
          description: payload.error || "agent 返回失败",
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      return payload;
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("下载目录检测失败", { description: msg });
      throw err;
    }
  };

  const runLaunchAction = async (
    gameID: string,
    action: AccountLaunchAction,
    fields: AccountLaunchActionFields,
  ) => {
    try {
      const payload = await apiPostForm<AccountLaunchActionResponse>(
        accountLaunchPath(accountId, gameID, action),
        fields,
      );
      const description = launchActionDescription(payload);
      if (payload.ok) {
        toast.success(accountLaunchActionLabel(action), {
          description,
        });
      } else {
        toast.error(`${accountLaunchActionLabel(action)}失败`, {
          description: payload.error || description || "agent 返回失败",
        });
      }
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      return payload;
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error(`${accountLaunchActionLabel(action)}失败`, {
        description: msg,
      });
      throw err;
    }
  };

  const saveRoleBinding = async (
    gameID: string,
    fields: RoleBindingForm,
  ) => {
    const slotID = fields.slot_id?.trim() ?? "";
    const targetServerID = fields.target_server_id?.trim() ?? "";
    if (!slotID || !targetServerID) {
      toast.error("绑定需要选择槽位并填写目标 server_id");
      throw new Error("slot_id and target_server_id required");
    }
    const existing = (roleBindingsQ.data?.items ?? []).find(
      (binding) =>
        binding.game_id === gameID &&
        binding.server_id === targetServerID,
    );
    try {
      let bindingID = existing?.id ?? 0;
      if (existing) {
        if (existing.role_id) {
          throw new Error(
            `该区服已有角色绑定在槽位 ${existing.slot_id}，不能在账号页改绑已绑定角色`,
          );
        }
        const updated = await apiPostForm<{ ok: boolean; binding: RoleLaunchBindingEntry }>(
          roleLaunchBindingPath(existing.id),
          {
            account_id: accountId,
            game_id: gameID,
            server_id: targetServerID,
            expected_role_name: fields.expected_role_name?.trim() || existing.expected_role_name || "",
            slot_id: slotID,
            status: existing.status === "disabled" ? "pending" : existing.status,
            desired_start_enabled: "1",
          },
        );
        bindingID = updated.binding.id;
      } else if (!bindingID) {
        const created = await apiPostForm<{ ok: boolean; binding: RoleLaunchBindingEntry }>(
          roleLaunchBindingsPath(),
          {
            account_id: accountId,
            game_id: gameID,
            server_id: targetServerID,
            slot_id: slotID,
            expected_role_name: fields.expected_role_name?.trim() || "",
            status: "pending",
            desired_start_enabled: "1",
          },
        );
        bindingID = created.binding.id;
      }
      toast.success("角色启动绑定已保存", {
        description: `binding #${bindingID} · server ${targetServerID}`,
      });
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
      void queryClient.invalidateQueries({
        queryKey: ["role-launch-bindings", "account", accountId],
      });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("保存角色启动绑定失败", { description: msg });
      throw err;
    }
  };

  return (
    <div className="space-y-4">
      <Link to="/accounts" search={{}} className="text-xs underline">
        ← 返回列表
      </Link>

      <QueryView query={q}>
        {(data) => (
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList>
              <TabsTrigger value="base">基础</TabsTrigger>
              <TabsTrigger value="auths">
                授权 ({data.auths.length})
              </TabsTrigger>
              <TabsTrigger value="modules">模块</TabsTrigger>
              <TabsTrigger value="events">
                事件 ({data.events.length})
              </TabsTrigger>
              <TabsTrigger value="audit">
                审计 ({data.audit.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="base">
              <AccountBaseCard
                data={data}
                canWrite={canWrite}
                onSaveProfile={saveProfile}
                onMarketCDPAction={runMarketCDPAction}
                onProbeDownloadDir={probeDownloadDir}
                onRefreshMemberBenefit={refreshMemberBenefit}
                onLaunchAction={runLaunchAction}
                onSaveRoleBinding={saveRoleBinding}
                roleBindings={roleBindingsQ.data?.items ?? []}
              />
            </TabsContent>

            <TabsContent value="auths">
              <AuthsTable
                auths={data.auths}
                accountId={accountId}
                canWrite={canWrite}
                actor={actor}
              />
            </TabsContent>

            <TabsContent value="modules">
              <p className="mb-2 text-xs text-[hsl(var(--muted-foreground))]">
                这里展示账号覆盖与任务运行状态；游戏默认配置在游戏详情页维护，配置偏差详情见{" "}
                <Link to="/modules" className="underline">
                  模块总览 →
                </Link>
              </p>
              <QueryView query={modulesQ}>
                {(modules) => (
                  <ModulesTable
                    rows={modules.rows}
                    configs={modules.configs}
                    accountId={accountId}
                  />
                )}
              </QueryView>
            </TabsContent>

            <TabsContent value="events">
              <EventTable items={data.events} />
            </TabsContent>

            <TabsContent value="audit">
              <div className="mb-2 text-xs text-[hsl(var(--muted-foreground))]">
                最近 {data.audit.length} 条 ·{" "}
                <Link
                  to="/audit"
                  search={{ domain: "account" }}
                  className="underline"
                >
                  查看全部账号审计 →
                </Link>
              </div>
              <AuditTable items={data.audit} />
            </TabsContent>
          </Tabs>
        )}
      </QueryView>
    </div>
  );
}

type AccountProfileForm = Record<
  | "phone"
  | "box_exe_path"
  | "market_cdp_agent_id"
  | "market_cdp_port"
  | "market_cdp_profile_dir",
  string
>;

type AccountLaunchActionFields = {
  target_server_id?: string;
  server_name?: string;
};

type RoleBindingForm = AccountLaunchActionFields & {
  slot_id?: string;
  expected_role_name?: string;
};

interface AccountMarketCDPActionResponse {
  ok: boolean;
  action: AccountMarketCDPAction;
  command: string;
  agent_id: string;
  error?: string;
  result?: {
    ok?: boolean;
    error?: string;
    result?: MarketCDPProcessResult;
    [key: string]: unknown;
  };
}

interface MarketCDPProcessResult {
  status?: string;
  port?: number;
  profile_dir?: string;
  running?: boolean;
  cdp_ready?: boolean;
  [key: string]: unknown;
}

function accountMarketCDPActionLabel(action: AccountMarketCDPAction) {
  switch (action) {
    case "start":
      return "启动 CDP";
    case "stop":
      return "停止 CDP";
    case "status":
    default:
      return "检查 CDP";
  }
}

function accountLaunchActionLabel(action: AccountLaunchAction) {
  switch (action) {
    case "refresh-env":
      return "刷新 env";
  }
}

function stringFromUnknown(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function numberFromUnknown(value: unknown): number | null {
  return typeof value === "number" && Number.isFinite(value) ? value : null;
}

function formatCouponAmount(value: number | undefined | null) {
  return typeof value === "number" && Number.isFinite(value)
    ? String(value)
    : "未采集";
}

function formatCouponExpiry(data: AccountDetailResponse["member_benefit"]) {
  if (!data) return "未采集";
  if (
    typeof data.nearest_expiring_coupon_amount === "number" &&
    data.nearest_expiring_coupon_at
  ) {
    return `${data.nearest_expiring_coupon_amount} 元 / ${data.nearest_expiring_coupon_at}`;
  }
  return data.status === "ok" ? "暂无即将过期提示" : "未采集";
}

function recordFromUnknown(value: unknown): Record<string, unknown> {
  return value && typeof value === "object"
    ? (value as Record<string, unknown>)
    : {};
}

function launchActionDescription(payload: AccountLaunchActionResponse) {
  const result = recordFromUnknown(payload.result);
  const nested = recordFromUnknown(result.result);
  const detail = Object.keys(nested).length > 0 ? nested : result;
  const pid = numberFromUnknown(detail.pid);
  const resourceRoot = stringFromUnknown(detail.resource_root);
  return [
    payload.agent_id ? `agent ${payload.agent_id}` : "",
    pid ? `pid ${pid}` : "",
    resourceRoot,
  ]
    .filter(Boolean)
    .join(" · ");
}

function envMetadataText(cache?: AccountGameCacheEntry) {
  if (!cache?.has_env_snapshot) return "未刷新";
  const parts = [
    cache.env_sha256 ? cache.env_sha256.slice(0, 8) : "",
    cache.env_size ? `${cache.env_size}B` : "",
    cache.env_updated_at
      ? formatMs(cache.env_updated_at)
      : cache.env_mtime_ms
        ? formatMs(cache.env_mtime_ms)
        : "",
    cache.has_env_b64 ? "含内容" : "仅元数据",
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(" · ") : "未刷新";
}

function cacheSlotLabel(cache: AccountGameCacheSlotEntry) {
  const slot = cache.slot_key || (cache.slot_id ? `slot ${cache.slot_id}` : "");
  const agent = cache.slot_agent_id || cache.agent_id || "";
  return [slot, agent, cache.resource_root].filter(Boolean).join(" · ");
}

function slotValue(cache: AccountGameCacheSlotEntry) {
  return (cache.slot_id ?? 0) > 0 ? String(cache.slot_id) : "";
}

function gameBindingKey(gameID: string, serverID: string) {
  return `${gameID.trim()}|${serverID.trim()}`;
}

function roleBindingFormValue(
  forms: Record<string, RoleBindingForm>,
  gameID: string,
  binding?: RoleLaunchBindingEntry,
) {
  const key = gameBindingKey(gameID, binding?.server_id ?? "");
  return forms[key] ?? {
    target_server_id: binding?.server_id ?? "",
    expected_role_name: binding?.expected_role_name || binding?.role_name || "",
    slot_id: binding?.slot_id ? String(binding.slot_id) : "",
  };
}

function bindingsForGame(bindings: RoleLaunchBindingEntry[], gameID: string) {
  return bindings
    .filter((binding) => binding.game_id === gameID)
    .sort((a, b) => a.server_id.localeCompare(b.server_id) || a.id - b.id);
}

function MemberBenefitPanel({ data }: { data: AccountDetailResponse }) {
  const benefit = data.member_benefit;
  if (!benefit) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        暂未采集会员与充值券快照。
      </p>
    );
  }

  const badgeVariant =
    benefit.status === "ok"
      ? "ok"
      : benefit.status === "partial"
        ? "warn"
        : benefit.status === "failed"
          ? "danger"
          : "neutral";

  return (
    <div className="grid gap-3 text-xs md:grid-cols-2 xl:grid-cols-5">
      <div>
        <div className="text-[hsl(var(--muted-foreground))]">会员到期</div>
        <div className="mt-1 font-mono">
          {benefit.member_expire_at || "未采集"}
        </div>
      </div>
      <div>
        <div className="text-[hsl(var(--muted-foreground))]">当前充值券</div>
        <div className="mt-1 font-mono">
          {formatCouponAmount(benefit.owned_coupon_amount)}
        </div>
      </div>
      <div>
        <div className="text-[hsl(var(--muted-foreground))]">还可领取</div>
        <div className="mt-1 font-mono">
          {formatCouponAmount(benefit.claimable_coupon_amount)}
        </div>
      </div>
      <div>
        <div className="text-[hsl(var(--muted-foreground))]">最近过期</div>
        <div className="mt-1 font-mono">{formatCouponExpiry(benefit)}</div>
      </div>
      <div>
        <div className="text-[hsl(var(--muted-foreground))]">采集状态</div>
        <div className="mt-1 flex flex-wrap items-center gap-2">
          <StatusBadge variant={badgeVariant}>
            {benefit.status || "unknown"}
          </StatusBadge>
          <span className="font-mono text-[hsl(var(--muted-foreground))]">
            {benefit.observed_at ? formatMs(benefit.observed_at) : "未刷新"}
          </span>
        </div>
      </div>
      {benefit.error ? (
        <div className="text-[hsl(var(--destructive))] md:col-span-2 xl:col-span-5">
          {benefit.error}
        </div>
      ) : null}
    </div>
  );
}

function AccountBaseCard({
  data,
  canWrite,
  onSaveProfile,
  onMarketCDPAction,
  onProbeDownloadDir,
  onRefreshMemberBenefit,
  onLaunchAction,
  onSaveRoleBinding,
  roleBindings,
}: {
  data: AccountDetailResponse;
  canWrite: boolean;
  onSaveProfile: (profile: AccountProfileForm) => Promise<void>;
  onMarketCDPAction: (
    action: AccountMarketCDPAction,
  ) => Promise<AccountMarketCDPActionResponse>;
  onProbeDownloadDir: () => Promise<AccountDownloadDirProbeResponse>;
  onRefreshMemberBenefit: () => Promise<AccountMemberBenefitRefreshResponse>;
  onLaunchAction: (
    gameID: string,
    action: AccountLaunchAction,
    fields: AccountLaunchActionFields,
  ) => Promise<AccountLaunchActionResponse>;
  onSaveRoleBinding: (
    gameID: string,
    fields: RoleBindingForm,
  ) => Promise<void>;
  roleBindings: RoleLaunchBindingEntry[];
}) {
  const [phone, setPhone] = useState(data.account.phone ?? "");
  const [boxExePath, setBoxExePath] = useState(
    data.account.box_exe_path ?? "",
  );
  const [marketCDPAgentID, setMarketCDPAgentID] = useState(
    data.account.market_cdp_agent_id ?? "",
  );
  const [marketCDPPort, setMarketCDPPort] = useState(
    data.account.market_cdp_port ? String(data.account.market_cdp_port) : "",
  );
  const [marketCDPProfileDir, setMarketCDPProfileDir] = useState(
    data.account.market_cdp_profile_dir ?? "",
  );
  const [saving, setSaving] = useState(false);
  const [cdpAction, setCDPAction] = useState<AccountMarketCDPAction | null>(
    null,
  );
  const [probingDownloadDir, setProbingDownloadDir] = useState(false);
  const [refreshingMemberBenefit, setRefreshingMemberBenefit] = useState(false);
  const [launchAction, setLaunchAction] = useState<{
    gameID: string;
    action: AccountLaunchAction | "save-binding";
  } | null>(null);
  const [bindingForms, setBindingForms] = useState<
    Record<string, RoleBindingForm>
  >({});
  const gameCaches = Array.isArray(data.game_caches) ? data.game_caches : [];
  const dirty =
    phone.trim() !== (data.account.phone ?? "") ||
    boxExePath.trim() !== (data.account.box_exe_path ?? "") ||
    marketCDPAgentID.trim() !== (data.account.market_cdp_agent_id ?? "") ||
    marketCDPPort.trim() !==
      (data.account.market_cdp_port ? String(data.account.market_cdp_port) : "") ||
    marketCDPProfileDir.trim() !==
      (data.account.market_cdp_profile_dir ?? "");

  useEffect(() => {
    setPhone(data.account.phone ?? "");
    setBoxExePath(data.account.box_exe_path ?? "");
    setMarketCDPAgentID(data.account.market_cdp_agent_id ?? "");
    setMarketCDPPort(
      data.account.market_cdp_port ? String(data.account.market_cdp_port) : "",
    );
    setMarketCDPProfileDir(data.account.market_cdp_profile_dir ?? "");
  }, [
    data.account.phone,
    data.account.box_exe_path,
    data.account.market_cdp_agent_id,
    data.account.market_cdp_port,
    data.account.market_cdp_profile_dir,
  ]);

  async function save() {
    setSaving(true);
    try {
      await onSaveProfile({
        phone: phone.trim(),
        box_exe_path: boxExePath.trim(),
        market_cdp_agent_id: marketCDPAgentID.trim(),
        market_cdp_port: marketCDPPort.trim(),
        market_cdp_profile_dir: marketCDPProfileDir.trim(),
      });
    } catch {
      // onSaveProfile 已经 toast 错误，这里只负责复位按钮状态。
    } finally {
      setSaving(false);
    }
  }

  async function runCDPAction(action: AccountMarketCDPAction) {
    if (action === "stop") {
      const ok = window.confirm(
        "停止 996box CDP 后，下次重新启动 996box 需要重新登录该账号。只在维护或误开端口时停止。",
      );
      if (!ok) return;
    }
    setCDPAction(action);
    try {
      await onMarketCDPAction(action);
    } catch {
      // onMarketCDPAction 已经 toast 错误。
    } finally {
      setCDPAction(null);
    }
  }

  async function probeDownloadDir() {
    setProbingDownloadDir(true);
    try {
      await onProbeDownloadDir();
    } catch {
      // onProbeDownloadDir 已经 toast 错误。
    } finally {
      setProbingDownloadDir(false);
    }
  }

  async function refreshMemberBenefit() {
    setRefreshingMemberBenefit(true);
    try {
      await onRefreshMemberBenefit();
    } catch {
      // onRefreshMemberBenefit 已经 toast 错误。
    } finally {
      setRefreshingMemberBenefit(false);
    }
  }

  function updateBindingForm(
    gameID: string,
    serverID: string,
    key: keyof RoleBindingForm,
    value: string,
  ) {
    setBindingForms((current) => {
      const currentKey = gameBindingKey(gameID, serverID);
      const currentForm = current[currentKey] ?? { target_server_id: serverID };
      const nextForm = { ...currentForm, [key]: value };
      const nextServerID =
        key === "target_server_id" ? value.trim() : serverID.trim();
      const nextKey = gameBindingKey(gameID, nextServerID);
      const next = { ...current };
      delete next[currentKey];
      next[nextKey] = nextForm;
      return next;
    });
  }

  function addBindingForm(gameID: string) {
    setBindingForms((current) => {
      const key = gameBindingKey(gameID, "");
      if (current[key]) return current;
      return {
        ...current,
        [key]: {
          target_server_id: "",
          expected_role_name: "",
          slot_id: "",
        },
      };
    });
  }

  function removeDraftBinding(gameID: string) {
    setBindingForms((current) => {
      const next = { ...current };
      delete next[gameBindingKey(gameID, "")];
      return next;
    });
  }

  async function refreshEnv(gameID: string) {
    const normalizedGameID = gameID.trim();
    if (!normalizedGameID) {
      toast.error("game_id 不能为空");
      return;
    }
    setLaunchAction({ gameID: normalizedGameID, action: "refresh-env" });
    try {
      await onLaunchAction(normalizedGameID, "refresh-env", {});
    } catch {
      // onLaunchAction 已经 toast 错误。
    } finally {
      setLaunchAction(null);
    }
  }

  async function saveBinding(gameID: string, binding?: RoleLaunchBindingEntry) {
    const form = roleBindingFormValue(bindingForms, gameID, binding);
    if (!form.target_server_id?.trim() || !form.slot_id?.trim()) {
      toast.error("请填写 server_id 并选择固定槽位");
      return;
    }
    setLaunchAction({ gameID, action: "save-binding" });
    try {
      await onSaveRoleBinding(gameID, form);
      if (!binding) {
        removeDraftBinding(gameID);
      }
    } catch {
      // onSaveRoleBinding 已经 toast 错误。
    } finally {
      setLaunchAction(null);
    }
  }

  const cdpBindingReady =
    marketCDPAgentID.trim() !== "" && marketCDPPort.trim() !== "";
  const boxReady = boxExePath.trim() !== "";
  const cdpButtonsDisabled =
    !canWrite || !cdpBindingReady || !boxReady || cdpAction !== null;
  const downloadDirProbeDisabled =
    !canWrite ||
    dirty ||
    !cdpBindingReady ||
    cdpAction !== null ||
    probingDownloadDir;
  const memberBenefitRefreshDisabled =
    !canWrite ||
    !cdpBindingReady ||
    !boxReady ||
    cdpAction !== null ||
    probingDownloadDir ||
    refreshingMemberBenefit;
  const actionDisabled = !canWrite || launchAction !== null;

  return (
    <Card>
      <CardHeader>
        <CardTitle>{data.account.game_account}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <dl className="grid grid-cols-[140px_1fr] gap-y-1 text-xs">
          <dt className="text-[hsl(var(--muted-foreground))]">id</dt>
          <dd className="font-mono">{data.account.id}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">game_id</dt>
          <dd className="font-mono">{data.account.game_id ?? ""}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">账号生命周期</dt>
          <dd>{data.account.status ?? ""}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">created_at</dt>
          <dd className="font-mono">{formatMs(data.account.created_at)}</dd>
          <dt className="text-[hsl(var(--muted-foreground))]">updated_at</dt>
          <dd className="font-mono">{formatMs(data.account.updated_at)}</dd>
        </dl>

        <div className="grid gap-3 lg:grid-cols-2 xl:grid-cols-[minmax(220px,1fr)_minmax(320px,1.4fr)_minmax(220px,1fr)_140px_minmax(280px,1.2fr)]">
          <Field label="交易行手机号">
            <Input
              value={phone}
              disabled={!canWrite || saving || cdpAction !== null}
              onChange={(event) => setPhone(event.target.value)}
              placeholder="用于交易行验证码发送"
              inputMode="tel"
            />
          </Field>
          <Field label="996box.exe">
            <Input
              value={boxExePath}
              disabled={!canWrite || saving || cdpAction !== null}
              onChange={(event) => setBoxExePath(event.target.value)}
              placeholder={String.raw`D:\996box\accountA\996box.exe`}
              className="font-mono"
            />
          </Field>
          <Field label="兼容 CDP agent ID">
            <Input
              value={marketCDPAgentID}
              disabled={!canWrite || saving || cdpAction !== null}
              onChange={(event) => setMarketCDPAgentID(event.target.value)}
              placeholder="例如 DESKTOP-FLMOO3A"
            />
          </Field>
          <Field label="兼容 CDP 端口">
            <Input
              value={marketCDPPort}
              disabled={!canWrite || saving || cdpAction !== null}
              onChange={(event) => setMarketCDPPort(event.target.value)}
              placeholder="例如 9223"
              inputMode="numeric"
            />
          </Field>
          <Field label="兼容 CDP profile 目录">
            <Input
              value={marketCDPProfileDir}
              disabled={!canWrite || saving || cdpAction !== null}
              onChange={(event) => setMarketCDPProfileDir(event.target.value)}
              placeholder="留空则 agent 按账号生成固定目录"
            />
          </Field>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            disabled={!canWrite || saving || cdpAction !== null || !dirty}
            onClick={() => void save()}
          >
            保存资料
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={cdpButtonsDisabled}
            onClick={() => void runCDPAction("status")}
          >
            {cdpAction === "status" ? "检查中..." : "检查 CDP"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={cdpButtonsDisabled}
            onClick={() => void runCDPAction("start")}
          >
            {cdpAction === "start" ? "启动中..." : "启动 CDP"}
          </Button>
          <Button
            size="sm"
            variant="destructive"
            disabled={cdpButtonsDisabled}
            onClick={() => void runCDPAction("stop")}
          >
            {cdpAction === "stop" ? "停止中..." : "停止 CDP"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={downloadDirProbeDisabled}
            onClick={() => void probeDownloadDir()}
          >
            {probingDownloadDir ? "检测中..." : "检测下载目录"}
          </Button>
        </div>
        <div className="space-y-1 text-xs text-[hsl(var(--muted-foreground))]">
          <p>
            账号目录只承载 996box 登录态、CDP 和 downloadDir；游戏客户端从固定 launch slot 启动。
          </p>
          <p>
            最近下载目录：{" "}
            <span className="font-mono text-[hsl(var(--foreground))]">
              {data.account.box_download_dir || "未检测"}
            </span>
            {data.account.box_download_dir_checked_at ? (
              <>
                {" "}
                · {formatMs(data.account.box_download_dir_checked_at)}
              </>
            ) : null}
            {data.account.box_download_dir_source ? (
              <> · {data.account.box_download_dir_source}</>
            ) : null}
          </p>
        </div>

        <div className="space-y-3 border-t border-[hsl(var(--border))] pt-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <h3 className="text-sm font-medium">会员与充值券</h3>
              <p className="text-xs text-[hsl(var(--muted-foreground))]">
                来自账号绑定的 996box CDP 登录态，最近过期券为空表示页面没有即将过期提示。
              </p>
            </div>
            <Button
              size="sm"
              variant="outline"
              disabled={memberBenefitRefreshDisabled}
              onClick={() => void refreshMemberBenefit()}
            >
              {refreshingMemberBenefit ? "刷新中..." : "刷新"}
            </Button>
          </div>
          <MemberBenefitPanel data={data} />
        </div>

        <AccountGameCachesPanel
          caches={gameCaches}
          roleBindings={roleBindings}
          bindingForms={bindingForms}
          launchAction={launchAction}
          actionDisabled={actionDisabled}
          cdpAction={cdpAction}
          cdpBindingReady={cdpBindingReady}
          boxReady={boxReady}
          probingDownloadDir={probingDownloadDir}
          onAddBinding={addBindingForm}
          onRemoveDraftBinding={removeDraftBinding}
          onUpdateBindingForm={updateBindingForm}
          onRefreshEnv={refreshEnv}
          onSaveBinding={saveBinding}
        />
      </CardContent>
    </Card>
  );
}

interface AccountGameCachesPanelProps {
  caches: AccountGameCacheEntry[];
  roleBindings: RoleLaunchBindingEntry[];
  bindingForms: Record<string, RoleBindingForm>;
  launchAction: {
    gameID: string;
    action: AccountLaunchAction | "save-binding";
  } | null;
  actionDisabled: boolean;
  cdpAction: AccountMarketCDPAction | null;
  cdpBindingReady: boolean;
  boxReady: boolean;
  probingDownloadDir: boolean;
  onAddBinding: (gameID: string) => void;
  onRemoveDraftBinding: (gameID: string) => void;
  onUpdateBindingForm: (
    gameID: string,
    serverID: string,
    key: keyof RoleBindingForm,
    value: string,
  ) => void;
  onRefreshEnv: (gameID: string) => void;
  onSaveBinding: (
    gameID: string,
    binding?: RoleLaunchBindingEntry,
  ) => void;
}

function AccountGameCachesPanel({
  caches,
  roleBindings,
  bindingForms,
  launchAction,
  actionDisabled,
  cdpAction,
  cdpBindingReady,
  boxReady,
  probingDownloadDir,
  onAddBinding,
  onRemoveDraftBinding,
  onUpdateBindingForm,
  onRefreshEnv,
  onSaveBinding,
}: AccountGameCachesPanelProps) {
  return (
    <div className="space-y-3 border-t border-[hsl(var(--border))] pt-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium">账号游戏缓存 / env</h3>
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            cache 来自账号 downloadDir 下的 agent 上报；server_id 属于角色绑定，不作为账号缓存字段。
          </p>
        </div>
      </div>
      {caches.length === 0 ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          暂未发现账号游戏缓存。先检测下载目录，并等待对应 agent 上报 launch slot cache。
        </p>
      ) : (
        <Table dense>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[110px]">game_id</TableHead>
              <TableHead className="min-w-[260px]">cache / slot</TableHead>
              <TableHead className="min-w-[220px]">env</TableHead>
              <TableHead className="min-w-[520px]">角色启动绑定</TableHead>
              <TableHead className="min-w-[130px]">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {caches.map((cache) => {
              const cacheSlots = Array.isArray(cache.caches) ? cache.caches : [];
              const actionKey = launchAction
                ? `${launchAction.gameID}:${launchAction.action}`
                : "";
              const gameBindings = bindingsForGame(roleBindings, cache.game_id);
              const hasDraft =
                bindingForms[gameBindingKey(cache.game_id, "")] !== undefined;
              const rows = [
                ...gameBindings.map((binding) => ({ binding })),
                ...(hasDraft ? [{ binding: undefined }] : []),
              ];
              return (
                <TableRow key={cache.game_id}>
                  <TableCell className="align-top">
                    <div className="font-mono">{cache.game_id}</div>
                    {cache.game_name ? (
                      <div className="mt-1 text-[10px] text-[hsl(var(--muted-foreground))]">
                        {cache.game_name}
                      </div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="text-xs">
                      {cache.cache_count} cache · {cache.slot_count} slot
                    </div>
                    <div className="mt-1 space-y-1 font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                      {cacheSlots.length === 0 ? (
                        <div>仅 env 快照</div>
                      ) : (
                        cacheSlots.map((slot) => (
                          <div key={`${slot.machine_cache_id}:${slot.resource_root}`}>
                            {cacheSlotLabel(slot)}
                          </div>
                        ))
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="align-top font-mono text-[10px] text-[hsl(var(--muted-foreground))]">
                    <div>{envMetadataText(cache)}</div>
                    {cache.env_source_download_dir ? (
                      <div className="mt-1">{cache.env_source_download_dir}</div>
                    ) : null}
                  </TableCell>
                  <TableCell className="align-top">
                    {rows.length === 0 ? (
                      <p className="text-xs text-[hsl(var(--muted-foreground))]">
                        暂无绑定。
                      </p>
                    ) : null}
                    <div className="space-y-2">
                      {rows.map(({ binding }) => {
                        const form = roleBindingFormValue(
                          bindingForms,
                          cache.game_id,
                          binding,
                        );
                        const serverID =
                          binding?.server_id ?? form.target_server_id ?? "";
                        const bindingKey = binding ? String(binding.id) : "draft";
                        return (
                          <div
                            key={bindingKey}
                            className="grid gap-2 md:grid-cols-[120px_150px_minmax(180px,1fr)_120px]"
                          >
                            <Input
                              value={form.target_server_id ?? ""}
                              disabled={actionDisabled || Boolean(binding?.role_id)}
                              onChange={(event) =>
                                onUpdateBindingForm(
                                  cache.game_id,
                                  serverID,
                                  "target_server_id",
                                  event.target.value,
                                )
                              }
                              placeholder="server_id"
                              className="h-8 font-mono text-xs"
                            />
                            <Input
                              value={form.expected_role_name ?? ""}
                              disabled={actionDisabled || Boolean(binding?.role_id)}
                              onChange={(event) =>
                                onUpdateBindingForm(
                                  cache.game_id,
                                  serverID,
                                  "expected_role_name",
                                  event.target.value,
                                )
                              }
                              placeholder="角色名"
                              className="h-8 text-xs"
                            />
                            <Select
                              value={form.slot_id ?? ""}
                              onValueChange={(value) =>
                                onUpdateBindingForm(
                                  cache.game_id,
                                  serverID,
                                  "slot_id",
                                  value,
                                )
                              }
                              disabled={
                                actionDisabled ||
                                Boolean(binding?.role_id) ||
                                cacheSlots.every((slot) => (slot.slot_id ?? 0) <= 0)
                              }
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="选择 launch slot" />
                              </SelectTrigger>
                              <SelectContent>
                                {cacheSlots
                                  .filter((slot) => (slot.slot_id ?? 0) > 0)
                                  .map((slot) => (
                                    <SelectItem
                                      key={slot.slot_id}
                                      value={slotValue(slot)}
                                    >
                                      {cacheSlotLabel(slot)}
                                    </SelectItem>
                                  ))}
                              </SelectContent>
                            </Select>
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={actionDisabled}
                              onClick={() => onSaveBinding(cache.game_id, binding)}
                            >
                              {actionKey === `${cache.game_id}:save-binding`
                                ? "保存中..."
                                : binding
                                  ? "更新绑定"
                                  : "保存绑定"}
                            </Button>
                            {binding ? (
                              <div className="text-[10px] text-[hsl(var(--muted-foreground))] md:col-span-4">
                                binding #{binding.id} · {binding.status}
                                {binding.role_name ? ` · ${binding.role_name}` : ""}
                              </div>
                            ) : (
                              <div className="md:col-span-4">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  disabled={actionDisabled}
                                  onClick={() => onRemoveDraftBinding(cache.game_id)}
                                >
                                  取消
                                </Button>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </TableCell>
                  <TableCell className="align-top">
                    <div className="flex flex-wrap gap-1">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={
                          actionDisabled ||
                          cdpAction !== null ||
                          probingDownloadDir ||
                          !cdpBindingReady ||
                          !boxReady
                        }
                        onClick={() => onRefreshEnv(cache.game_id)}
                      >
                        {actionKey === `${cache.game_id}:refresh-env`
                          ? "刷新中..."
                          : "刷新 env"}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={actionDisabled}
                        onClick={() => onAddBinding(cache.game_id)}
                      >
                        新增绑定
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}

interface AuthsTableProps {
  auths: AccountListEntry[];
  accountId: string;
  canWrite: boolean;
  actor: string;
}

function AuthsTable({ auths, accountId, canWrite, actor }: AuthsTableProps) {
  const queryClient = useQueryClient();
  async function saveMetadata(authID: number, note: string, tags: string) {
    try {
      await apiPostForm(accountAuthMetadataPath(authID), { note, tags });
      toast.success("已保存");
      void queryClient.invalidateQueries({ queryKey: ["account", accountId] });
    } catch (err) {
      const msg =
        err instanceof APIError
          ? err.message
          : err instanceof Error
            ? err.message
            : String(err);
      toast.error("保存失败", { description: msg });
      throw err;
    }
  }

  if (auths.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        该账号没有 auth 记录。Lua 客户端首次反连 8173 会自动写一行 pending 等审批。
      </p>
    );
  }

  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>授权 ID</TableHead>
          <TableHead>dev auth id</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>备注</TableHead>
          <TableHead>标签</TableHead>
          <TableHead>最后出现</TableHead>
          <TableHead>操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {auths.map((a) => (
          <TableRow key={a.id}>
            <TableCell className="font-mono">{a.id}</TableCell>
            <TableCell className="font-mono">{a.mac_id}</TableCell>
            <TableCell>
              <StatusBadge
                variant={
                  a.status === "active" || a.status === "verified"
                    ? "ok"
                    : a.status === "pending"
                      ? "warn"
                      : a.status === "revoked"
                        ? "danger"
                        : "neutral"
                }
              >
                {a.status}
              </StatusBadge>
            </TableCell>
            <TableCell>
              <InlineEditableCell
                value={a.note ?? ""}
                disabled={!canWrite}
                ariaLabel={`note-${a.id}`}
                onSave={(next) => saveMetadata(a.id, next, a.tags ?? "")}
              />
            </TableCell>
            <TableCell>
              <InlineEditableCell
                value={a.tags ?? ""}
                disabled={!canWrite}
                ariaLabel={`tags-${a.id}`}
                onSave={(next) => saveMetadata(a.id, a.note ?? "", next)}
              />
            </TableCell>
            <TableCell className="text-[hsl(var(--muted-foreground))]">
              {a.last_seen_at ? formatMs(a.last_seen_at) : ""}
            </TableCell>
            <TableCell>
              <AuthActionsCell
                domain="account"
                authID={a.id}
                status={a.status}
                subject={a.subject}
                actor={actor}
                canWrite={canWrite}
                onChanged={() =>
                  void queryClient.invalidateQueries({
                    queryKey: ["account", accountId],
                  })
                }
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface ModulesTableProps {
  rows: ModuleMatrixEntry[];
  configs: AccountModulesResponse["configs"];
  accountId: string;
}

function ModulesTable({ rows, configs, accountId }: ModulesTableProps) {
  if (rows.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        该账号目前没有模块状态。Lua 客户端上报模块快照后会自动出现。
      </p>
    );
  }
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>模块</TableHead>
          <TableHead>状态</TableHead>
          <TableHead>上报开关</TableHead>
          <TableHead>运行状态</TableHead>
          <TableHead>配置一致性</TableHead>
          <TableHead>最近错误</TableHead>
          <TableHead>更新时间</TableHead>
          <TableHead>操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const config = configs.find((c) => c.module === row.module);
          return (
            <TableRow key={row.module}>
              <TableCell className="font-mono">{row.module}</TableCell>
              <TableCell>
                <StatusBadge
                  variant={
                    row.color_state === "green"
                      ? "ok"
                      : row.color_state === "red"
                        ? "danger"
                        : row.color_state === "yellow"
                          ? "warn"
                          : "neutral"
                  }
                >
                  {formatModuleMatrixColor(row.color_state)}
                </StatusBadge>
              </TableCell>
              <TableCell>
                {formatModuleReportedEnabled(
                  row.desired_enabled,
                  row.runtime_status,
                )}
              </TableCell>
              <TableCell>{formatModuleRuntimeStatus(row.runtime_status) || "—"}</TableCell>
              <TableCell>
                <span className="text-xs">
                  {formatModuleConfigDrift(row.config_drift)}
                </span>
                {config?.desired_config_hash && config?.current_config_hash ? (
                  <span className="ml-2 text-[10px] text-[hsl(var(--muted-foreground))]">
                    {config.current_config_hash.slice(0, 7)} →{" "}
                    {config.desired_config_hash.slice(0, 7)}
                  </span>
                ) : null}
              </TableCell>
              <TableCell className="text-[hsl(var(--muted-foreground))]">
                {row.last_command_error ?? "—"}
              </TableCell>
              <TableCell className="text-[hsl(var(--muted-foreground))]">
                {row.last_updated_at
                  ? formatMs(row.last_updated_at)
                  : "未上报"}
              </TableCell>
              <TableCell>
                <Link
                  to="/accounts/$accountId/modules/$module/edit"
                  params={{ accountId, module: row.module }}
                  className="text-xs underline"
                >
                  编辑覆盖
                </Link>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

interface EventRow {
  id: number;
  event_type: string;
  created_at: number;
  payload_json: string;
}
function EventTable({ items }: { items: EventRow[] }) {
  if (items.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">暂无事件</p>
    );
  }
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>类型</TableHead>
          <TableHead>时间</TableHead>
          <TableHead>负载</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((e) => (
          <TableRow key={e.id}>
            <TableCell className="font-mono">{e.id}</TableCell>
            <TableCell className="font-mono">{e.event_type}</TableCell>
            <TableCell className="font-mono">{formatMs(e.created_at)}</TableCell>
            <TableCell className="font-mono text-[10px]">
              {e.payload_json.length > 80
                ? `${e.payload_json.slice(0, 80)}…`
                : e.payload_json}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

interface AuditRow {
  id: number;
  action: string;
  actor: string;
  created_at: number;
}
function AuditTable({ items }: { items: AuditRow[] }) {
  if (items.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">暂无审计</p>
    );
  }
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>ID</TableHead>
          <TableHead>动作</TableHead>
          <TableHead>操作者</TableHead>
          <TableHead>时间</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((e) => (
          <TableRow key={e.id}>
            <TableCell className="font-mono">{e.id}</TableCell>
            <TableCell className="font-mono">{e.action}</TableCell>
            <TableCell className="font-mono">{e.actor}</TableCell>
            <TableCell className="font-mono">{formatMs(e.created_at)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
